import React from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { Clock } from "lucide-react";
import { formatExchangeDisplay } from "@/utils/formatExchange";
import { TokenIcon } from "@/components/TokenIcon";

interface IncomingListingTileProps {
  alert: CryptoAlertData;
  onAnalyze?: (symbol: string) => void;
}

export const IncomingListingTile: React.FC<IncomingListingTileProps> = ({
  alert,
  onAnalyze,
}) => {
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleClick = () => onAnalyze?.(alert.currency);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center justify-between gap-4 p-4 bg-secondary border border-border rounded-xl hover:bg-tertiary cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <TokenIcon
          symbol={alert.currency}
          logo={alert.tokenMetadata?.logo}
          size="md"
        />
        <div className="min-w-0">
          <h3 className="font-semibold text-text-primary truncate">
            {alert.currency}
          </h3>
          <p className="text-xs text-text-secondary flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatExchangeDisplay(alert.exchange, alert.exchangeType)} ·{" "}
            {formatTimeAgo(alert.createdAt)}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <span className="px-3 py-1 rounded-lg bg-amber-500/20 text-amber-400 text-sm font-medium">
          Coming soon
        </span>
      </div>
    </div>
  );
};
